import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Collapse,
  LinearProgress,
} from '@mui/material';
import {
  HowToVote as VoteIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  SwapHoriz as SwapIcon,
} from '@mui/icons-material';
import DataService, { Candidate } from '../services/dataService';
import PersonaService from '../services/personaService';
import PersonaBuilder from './PersonaBuilder';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

/** Stable session UUID — persists across page reloads for the same browser session */
function getSessionId(): string {
  const key = 'electure_mock_session';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
  }
  return id;
}

interface DailyVoteProps {
  onVote?: (candidateId: number) => void;
  /** Topics/actors the user has already read — passed in from the host page */
  topicsRead?: string[];
  actorsViewed?: string[];
}

const DailyVote: React.FC<DailyVoteProps> = ({ onVote, topicsRead = [], actorsViewed = [] }) => {
  const [selectedCandidate, setSelectedCandidate] = useState<number | null>(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [votingOpen, setVotingOpen] = useState(false);
  const [personaBuilderOpen, setPersonaBuilderOpen] = useState(false);
  const [voteCountdown, setVoteCountdown] = useState<number>(0);
  const [totalVotes, setTotalVotes] = useState<number>(0);
  const [dailyVoteData, setDailyVoteData] = useState<any>(null);
  const [personaCreated, setPersonaCreated] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [currentPersona, setCurrentPersona] = useState<any>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Mock-vote deliberative polling state
  const [preVoteCandidate, setPreVoteCandidate] = useState<string | null>(null);
  const [postVoteCandidate, setPostVoteCandidate] = useState<string | null>(null);
  const [voteChanged, setVoteChanged] = useState<boolean | null>(null);
  const [mockResults, setMockResults] = useState<any>(null);
  const sessionStartTime = useRef<number>(Date.now());

  const personaService = new PersonaService();

  const partyColors: { [key: string]: string } = {
    'APC': '#2E7D32',
    'PDP': '#1565C0',
    'LP': '#6A1B9A',
    'NNPP': '#C62828',
    'SDP': '#F57C00',
    'ADC': '#00695C',
  };

  // Helper function for formatting numbers
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  useEffect(() => {
    const loadData = async () => {
      // Load candidates from DataService
      const candidatesData = await DataService.getCandidates();
      setCandidates(candidatesData);

      // Load daily vote data
      const dailyData = await DataService.getTodayDailyVote();
      setDailyVoteData(dailyData);
      setTotalVotes(dailyData?.totalVotes || 0);
    };
    loadData();

    // Check for existing persona
    const existingPersona = personaService.getCurrentPersona();
    if (existingPersona) {
      setCurrentPersona(existingPersona);
      // Check if this persona has already voted today
      const today = new Date().toDateString();
      const hasVotedToday = existingPersona.votes.some((vote: any) =>
        new Date(vote.timestamp).toDateString() === today
      );
      setHasVoted(hasVotedToday);
    }

    // Start countdown timer
    const startCountdown = () => {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);

      const timeUntilMidnight = tomorrow.getTime() - now.getTime();
      const hours = Math.floor(timeUntilMidnight / (1000 * 60 * 60));
      const minutes = Math.floor((timeUntilMidnight % (1000 * 60 * 60)) / (1000 * 60));

      setVoteCountdown(hours * 60 + minutes);
    };

    startCountdown();
    const timer = setInterval(startCountdown, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const fetchMockResults = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/mock-vote/results`);
      if (res.ok) {
        const data = await res.json();
        setMockResults(data);
      }
    } catch {
      // silently ignore — results are non-critical
    }
  };

  const handleVote = async () => {
    if (!selectedCandidate) return;

    if (!currentPersona) {
      setPersonaBuilderOpen(true);
      return;
    }

    const candidate = candidates.find(c => c.id === selectedCandidate);
    if (!candidate) return;

    // Phase 1 — pre-vote (first click)
    if (!preVoteCandidate) {
      setPreVoteCandidate(candidate.name);
      return;
    }

    // Phase 2 — post-vote (second click after exploring)
    const post = candidate.name;
    setPostVoteCandidate(post);

    const sessionId = getSessionId();
    const elapsed = Math.round((Date.now() - sessionStartTime.current) / 1000);

    try {
      const res = await fetch(`${API_BASE}/api/mock-vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          user_state: currentPersona.state || null,
          user_age_bracket: currentPersona.ageRange || null,
          user_gender: currentPersona.gender || null,
          pre_vote_candidate: preVoteCandidate,
          post_vote_candidate: post,
          topics_read: topicsRead,
          actors_viewed: actorsViewed,
          time_on_platform_seconds: elapsed,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setVoteChanged(data.vote_changed);
      }
    } catch {
      // fall through — still mark as voted locally
      setVoteChanged(preVoteCandidate !== post);
    }

    // Also record in persona service for local tracking
    personaService.recordVote(currentPersona.id, selectedCandidate, candidate.name, 'Daily Poll');
    setHasVoted(true);
    setTotalVotes(prev => prev + 1);
    await fetchMockResults();

    if (onVote) onVote(selectedCandidate);
  };

  const handlePersonaComplete = (personaData: any) => {
    console.log('Persona completed:', personaData);
    const newPersona = personaService.createPersona(personaData);
    console.log('New persona created:', newPersona);
    personaService.setCurrentPersona(newPersona.id);
    setCurrentPersona(newPersona);
    setPersonaBuilderOpen(false);
    setPersonaCreated(true);

    // Hide success message after 3 seconds
    setTimeout(() => setPersonaCreated(false), 3000);

    // Auto-vote after persona creation
    if (selectedCandidate) {
      console.log('Auto-voting for candidate:', selectedCandidate);
      setTimeout(() => handleVote(), 1000); // Small delay to show success message first
    }
  };

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // No active poll for today → render a quiet empty state instead of stale fallback data
  if (!dailyVoteData || !dailyVoteData.active) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight="bold">
          Daily Poll
        </Typography>
        <Typography variant="caption" color="text.secondary">
          — no poll today, check back tomorrow
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
        {/* Success Message */}
        {personaCreated && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Persona created successfully! You can now vote in the daily poll.
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: isExpanded ? 1.5 : 0 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Daily Poll
              </Typography>
              <Box sx={{
                px: 1,
                py: 0.25,
                backgroundColor: '#f8fafc',
                borderRadius: 1.5,
                border: '1px solid #e2e8f0',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
                  Ends in:
                </Typography>
                <Typography variant="caption" color="primary.main" fontWeight="900">
                  {formatTime(voteCountdown)}
                </Typography>
              </Box>
              {dailyVoteData.electionTitle && (
                <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {dailyVoteData.electionTitle}
                </Typography>
              )}
            </Box>
            {dailyVoteData.question && (
              <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, fontWeight: 500 }}>
                {dailyVoteData.question}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography variant="subtitle2" color="primary.main" fontWeight="900">
              {totalVotes.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              votes
            </Typography>
            <IconButton
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              size="small"
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ pt: 1 }}>


            {/* Candidates */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {candidates.map((candidate) => {
                  const color = partyColors[candidate.party] || '#64748b';

                  return (
                    <Box
                      key={candidate.id}
                      sx={{
                        p: 1.5,
                        border: selectedCandidate === candidate.id ? `2px solid ${color}` : '1px solid #e2e8f0',
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f8fafc' },
                        transition: 'all 0.2s ease',
                      }}
                      onClick={() => !hasVoted && setSelectedCandidate(candidate.id)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            src={candidate.profileImage}
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: color,
                              fontSize: 12,
                              fontWeight: 'bold'
                            }}
                          >
                            {candidate.avatar}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="700">
                              {candidate.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {candidate.party}
                              {Array.isArray(candidate.platform) && candidate.platform.length > 0 && (
                                <> • {candidate.platform[0]}</>
                              )}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block">Votes</Typography>
                            <Typography variant="body2" fontWeight="700">{formatNumber(dailyVoteData?.votes?.[candidate.id] || 0)}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block">Followers</Typography>
                            <Typography variant="body2" fontWeight="700">{formatNumber(candidate.followers || 0)}</Typography>
                          </Box>
                          <Typography variant="body2" fontWeight="700" sx={{
                            color: candidate.trend && candidate.trend.startsWith('+') ? 'success.main' : 'error.main',
                          }}>
                            {candidate.trend || '0%'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Deliberative polling UX */}
            {hasVoted && voteChanged !== null && (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, backgroundColor: voteChanged ? '#fef3c7' : '#f0fdf4', border: `1px solid ${voteChanged ? '#fcd34d' : '#86efac'}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {voteChanged ? (
                    <>
                      <SwapIcon color="warning" fontSize="small" />
                      <Typography variant="body2" fontWeight="700" color="warning.dark">
                        You changed your mind!
                      </Typography>
                    </>
                  ) : (
                    <>
                      <CheckIcon color="success" fontSize="small" />
                      <Typography variant="body2" fontWeight="700" color="success.dark">
                        You stayed with your choice
                      </Typography>
                    </>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {preVoteCandidate} → {postVoteCandidate}
                </Typography>
                {mockResults && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
                      Live mock poll — {mockResults.total_votes} responses
                    </Typography>
                    {mockResults.candidates?.map((c: any) => (
                      <Box key={c.candidate} sx={{ mt: 0.75 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                          <Typography variant="caption">{c.candidate}</Typography>
                          <Typography variant="caption" fontWeight="700">{c.pct}%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={c.pct} sx={{ height: 4, borderRadius: 2 }} />
                      </Box>
                    ))}
                    {mockResults.mind_change_rate !== undefined && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {mockResults.mind_change_rate}% of voters changed their mind after exploring
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {preVoteCandidate && !hasVoted && (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }} icon={false}>
                <Typography variant="caption">
                  <strong>Pre-vote recorded:</strong> {preVoteCandidate}. Explore the platform, then cast your final vote below.
                </Typography>
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              {hasVoted ? (
                <Button variant="outlined" disabled startIcon={<CheckIcon />} sx={{ borderRadius: 2 }}>
                  Voted Today
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleVote}
                  disabled={!selectedCandidate}
                  startIcon={preVoteCandidate ? <CheckIcon /> : <VoteIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  {preVoteCandidate ? 'Confirm Final Vote' : 'Register Pre-Vote'}
                </Button>
              )}
            </Box>
          </Box>
        </Collapse>

        {/* Voting Dialog */}
        <Dialog open={votingOpen} onClose={() => setVotingOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Typography variant="h6" fontWeight="bold">
              Confirm Your Vote
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              This is a daily poll. You can vote once per day. Your vote is anonymous and tracked by persona.
            </Alert>

            <Typography variant="body1" sx={{ mb: 2 }}>
              You are voting for:
            </Typography>

            {candidates.map((candidate) => {
              if (candidate.id === selectedCandidate) {
                const color = partyColors[candidate.party] || '#64748b';
                return (
                  <Box key={candidate.id} sx={{
                    p: 2,
                    backgroundColor: '#f8fafc',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    textAlign: 'center'
                  }}>
                    <Avatar src={candidate.profileImage} sx={{ mr: 2, bgcolor: color }}>
                      {candidate.avatar || candidate.name.charAt(0)}
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">
                      {candidate.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {candidate.party}
                    </Typography>
                  </Box>
                );
              }
              return null;
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVotingOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleVote}
              variant="contained"
              disabled={!selectedCandidate}
            >
              Confirm Vote
            </Button>
          </DialogActions>
        </Dialog>

        {/* Persona Builder Dialog */}
        <Dialog
          open={personaBuilderOpen}
          onClose={() => setPersonaBuilderOpen(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { m: 2 } }}
        >
          <PersonaBuilder
            onComplete={handlePersonaComplete}
            onCancel={() => setPersonaBuilderOpen(false)}
          />
        </Dialog>
    </Box>
  );
};

export default DailyVote;
